/**
 * useMedicationSelections Hook
 * Handles medication and vaccine selection state management
 */

import { useState, useCallback } from "react";
import { travelerService, type RecommendationSelection, type AdministrationSite } from "@/services/travelers";
import { medicationService, type AIRecommendationsResponse } from "@/services/medications";
import { apiCache } from "@/utils/api-cache";
import { parseMedicationId, getApiSource } from "@/utils/medicationIdParser";

export interface UseMedicationSelectionsOptions {
  travelerId: string | undefined;
  showAlert: (variant: "destructive" | "success" | "warning", title: string, message: string) => void;
}

export interface UseMedicationSelectionsReturn {
  medicationSelections: Record<string, boolean | null>;
  medicationNotes: Record<string, string>;
  refillInfos: Record<string, string>;
  administrationSites: Record<string, AdministrationSite | null>;
  quantities: Record<string, string>;
  pharmacistRecommendationNote: string;
  aiRecommendations: AIRecommendationsResponse | null;
  savingSelections: boolean;
  selectionVersion: number;
  setAiRecommendations: (rec: AIRecommendationsResponse | null) => void;
  handleMedicationSelectionChange: (
    medicationId: string,
    value: boolean | null,
    noteOverride?: string
  ) => Promise<void>;
  handleMedicationNoteChange: (medicationId: string, note: string) => void;
  handleRefillInfoChange: (medicationId: string, refillInfo: string) => void;
  handleAdministrationSiteChange: (medicationId: string, site: AdministrationSite | null) => void;
  handleQuantityChange: (medicationId: string, quantity: string) => void;
  handleRecommendationNoteChange: (note: string) => void;
  saveItemNote: (medicationId: string, note: string) => Promise<boolean>;
  saveItemDetails: (medicationId: string, note: string, refillInfo: string, administrationSite: AdministrationSite | null, quantity?: string) => Promise<boolean>;
  saveSelections: (manualSelectionsToSave?: RecommendationSelection[]) => Promise<void>;
  initializeFromRecommendations: (recommendations: AIRecommendationsResponse) => void;
}

export function useMedicationSelections({
  travelerId,
  showAlert,
}: UseMedicationSelectionsOptions): UseMedicationSelectionsReturn {
  const [medicationSelections, setMedicationSelections] = useState<
    Record<string, boolean | null>
  >({});
  const [medicationNotes, setMedicationNotes] = useState<Record<string, string>>({});
  const [refillInfos, setRefillInfos] = useState<Record<string, string>>({});
  const [administrationSites, setAdministrationSites] = useState<Record<string, AdministrationSite | null>>({});
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [pharmacistRecommendationNote, setPharmacistRecommendationNote] = useState("");
  const [aiRecommendations, setAiRecommendations] =
    useState<AIRecommendationsResponse | null>(null);
  const [savingSelections, setSavingSelections] = useState(false);
  const [selectionVersion, setSelectionVersion] = useState(0);

  const initializeFromRecommendations = useCallback(
    (recommendations: AIRecommendationsResponse) => {
      const initialSelections: Record<string, boolean | null> = {};
      const initialNotes: Record<string, string> = {};
      const initialRefillInfos: Record<string, string> = {};
      const initialAdministrationSites: Record<string, AdministrationSite | null> = {};
      const initialQuantities: Record<string, string> = {};

      // Process AI recommended vaccines
      recommendations.recommendation.vaccines.forEach((vaccine) => {
        const key = `ai-vaccine-${vaccine.protocol_id}`;
        if (vaccine.isSelected !== undefined) {
          initialSelections[key] = vaccine.isSelected;
        }
        // Check both flat structure and nested selectionDetails
        const note = (vaccine as any).pharmacistNote || vaccine.selectionDetails?.pharmacistNote;
        if (note) {
          initialNotes[key] = note;
        }
        const refillInfo = (vaccine as any).refillInfo || vaccine.selectionDetails?.refillInfo;
        if (refillInfo) {
          initialRefillInfos[key] = refillInfo;
        }
        const adminSite = (vaccine as any).administrationSite || vaccine.selectionDetails?.administrationSite;
        if (adminSite) {
          initialAdministrationSites[key] = adminSite;
        }
        const quantity = (vaccine as any).quantity || vaccine.selectionDetails?.quantity;
        if (quantity) {
          initialQuantities[key] = String(quantity);
        }
      });

      // Process AI recommended medications
      recommendations.recommendation.medications.forEach((medication) => {
        const key = `ai-medication-${medication.protocol_id}`;
        if (medication.isSelected !== undefined) {
          initialSelections[key] = medication.isSelected;
        }
        // Check both flat structure and nested selectionDetails
        const note = (medication as any).pharmacistNote || medication.selectionDetails?.pharmacistNote;
        if (note) {
          initialNotes[key] = note;
        }
        const refillInfo = (medication as any).refillInfo || medication.selectionDetails?.refillInfo;
        if (refillInfo) {
          initialRefillInfos[key] = refillInfo;
        }
        const adminSite = (medication as any).administrationSite || medication.selectionDetails?.administrationSite;
        if (adminSite) {
          initialAdministrationSites[key] = adminSite;
        }
        const quantity = (medication as any).quantity || medication.selectionDetails?.quantity;
        if (quantity) {
          initialQuantities[key] = String(quantity);
        }
      });

      // Process pharmacist-added vaccines
      const pharmacistAddedVaccines =
        recommendations.recommendation.pharmacistAddedVaccines || [];
      pharmacistAddedVaccines.forEach((vaccine) => {
        const key = `manual-vaccine-${vaccine.protocol_id}`;
        if (vaccine.isSelected !== undefined) {
          initialSelections[key] = vaccine.isSelected;
        }
        // Check both flat structure and nested selectionDetails
        const note = (vaccine as any).pharmacistNote || vaccine.selectionDetails?.pharmacistNote;
        if (note) {
          initialNotes[key] = note;
        }
        const refillInfo = (vaccine as any).refillInfo || vaccine.selectionDetails?.refillInfo;
        if (refillInfo) {
          initialRefillInfos[key] = refillInfo;
        }
        const adminSite = (vaccine as any).administrationSite || vaccine.selectionDetails?.administrationSite;
        if (adminSite) {
          initialAdministrationSites[key] = adminSite;
        }
        const quantity = (vaccine as any).quantity || vaccine.selectionDetails?.quantity;
        if (quantity) {
          initialQuantities[key] = String(quantity);
        }
      });

      // Process pharmacist-added medications
      const pharmacistAddedMedications =
        recommendations.recommendation.pharmacistAddedMedications || [];
      pharmacistAddedMedications.forEach((medication) => {
        const key = `manual-medication-${medication.protocol_id}`;
        if (medication.isSelected !== undefined) {
          initialSelections[key] = medication.isSelected;
        }
        // Check both flat structure and nested selectionDetails
        const note = (medication as any).pharmacistNote || medication.selectionDetails?.pharmacistNote;
        if (note) {
          initialNotes[key] = note;
        }
        const refillInfo = (medication as any).refillInfo || medication.selectionDetails?.refillInfo;
        if (refillInfo) {
          initialRefillInfos[key] = refillInfo;
        }
        const adminSite = (medication as any).administrationSite || medication.selectionDetails?.administrationSite;
        if (adminSite) {
          initialAdministrationSites[key] = adminSite;
        }
        const quantity = (medication as any).quantity || medication.selectionDetails?.quantity;
        if (quantity) {
          initialQuantities[key] = String(quantity);
        }
      });

      // Process not recommended vaccines
      const notRecVaccines =
        recommendations.recommendation.notRecommended?.vaccines || [];
      notRecVaccines.forEach((vaccine: any) => {
        const key = `not-rec-vaccine-${vaccine.protocol_id}`;

        // Check if this vaccine also exists in pharmacistAddedVaccines
        const matchingPharmacistAdded = pharmacistAddedVaccines.find(
          (pav) => pav.protocol_id === vaccine.protocol_id
        );

        if (vaccine.isSelected !== undefined) {
          initialSelections[key] = vaccine.isSelected;
        } else if (matchingPharmacistAdded?.isSelected !== undefined) {
          initialSelections[key] = matchingPharmacistAdded.isSelected;
        }

        // Check both flat structure and nested selectionDetails
        // Also check pharmacistAddedVaccines if details are not in notRecommended
        const note = vaccine.pharmacistNote ||
                     vaccine.selectionDetails?.pharmacistNote ||
                     (matchingPharmacistAdded as any)?.pharmacistNote ||
                     matchingPharmacistAdded?.selectionDetails?.pharmacistNote;
        if (note) {
          initialNotes[key] = note;
        }

        const refillInfo = vaccine.refillInfo ||
                          vaccine.selectionDetails?.refillInfo ||
                          (matchingPharmacistAdded as any)?.refillInfo ||
                          matchingPharmacistAdded?.selectionDetails?.refillInfo;
        if (refillInfo) {
          initialRefillInfos[key] = refillInfo;
        }

        const adminSite = vaccine.administrationSite ||
                         vaccine.selectionDetails?.administrationSite ||
                         (matchingPharmacistAdded as any)?.administrationSite ||
                         matchingPharmacistAdded?.selectionDetails?.administrationSite;
        if (adminSite) {
          initialAdministrationSites[key] = adminSite;
        }

        const quantity = vaccine.quantity ||
                        vaccine.selectionDetails?.quantity ||
                        (matchingPharmacistAdded as any)?.quantity ||
                        matchingPharmacistAdded?.selectionDetails?.quantity;
        if (quantity) {
          initialQuantities[key] = String(quantity);
        }
      });

      // Process not recommended medications
      const notRecMedications =
        recommendations.recommendation.notRecommended?.medications || [];
      notRecMedications.forEach((medication: any) => {
        const key = `not-rec-medication-${medication.protocol_id}`;

        // Check if this medication also exists in pharmacistAddedMedications
        const matchingPharmacistAdded = pharmacistAddedMedications.find(
          (pam) => pam.protocol_id === medication.protocol_id
        );

        if (medication.isSelected !== undefined) {
          initialSelections[key] = medication.isSelected;
        } else if (matchingPharmacistAdded?.isSelected !== undefined) {
          initialSelections[key] = matchingPharmacistAdded.isSelected;
        }

        // Check both flat structure and nested selectionDetails
        // Also check pharmacistAddedMedications if details are not in notRecommended
        const note = medication.pharmacistNote ||
                     medication.selectionDetails?.pharmacistNote ||
                     (matchingPharmacistAdded as any)?.pharmacistNote ||
                     matchingPharmacistAdded?.selectionDetails?.pharmacistNote;
        if (note) {
          initialNotes[key] = note;
        }

        const refillInfo = medication.refillInfo ||
                          medication.selectionDetails?.refillInfo ||
                          (matchingPharmacistAdded as any)?.refillInfo ||
                          matchingPharmacistAdded?.selectionDetails?.refillInfo;
        if (refillInfo) {
          initialRefillInfos[key] = refillInfo;
        }

        const adminSite = medication.administrationSite ||
                         medication.selectionDetails?.administrationSite ||
                         (matchingPharmacistAdded as any)?.administrationSite ||
                         matchingPharmacistAdded?.selectionDetails?.administrationSite;
        if (adminSite) {
          initialAdministrationSites[key] = adminSite;
        }

        const quantity = medication.quantity ||
                        medication.selectionDetails?.quantity ||
                        (matchingPharmacistAdded as any)?.quantity ||
                        matchingPharmacistAdded?.selectionDetails?.quantity;
        if (quantity) {
          initialQuantities[key] = String(quantity);
        }
      });

      setMedicationSelections(initialSelections);
      setMedicationNotes(initialNotes);
      setRefillInfos(initialRefillInfos);
      setAdministrationSites(initialAdministrationSites);
      setQuantities(initialQuantities);
      setAiRecommendations(recommendations);

      if (recommendations.recommendation.pharmacistNote) {
        setPharmacistRecommendationNote(recommendations.recommendation.pharmacistNote);
      }
    },
    []
  );

  const handleMedicationSelectionChange = useCallback(
    async (medicationId: string, value: boolean | null, noteOverride?: string) => {
      setMedicationSelections((prev) => ({
        ...prev,
        [medicationId]: value,
      }));

      if ((value === true || value === false) && travelerId && aiRecommendations) {
        try {
          const parsed = parseMedicationId(medicationId);
          if (!parsed) return;

          const selection: RecommendationSelection = {
            item_type: parsed.itemType,
            item_key: parsed.protocolId,
            action: value === true ? "select" : "deselect",
          };

          const note =
            noteOverride !== undefined ? noteOverride : medicationNotes[medicationId];
          if (note && note.trim()) {
            selection.note = note.trim();
          }

          const source = getApiSource(parsed);
          if (source) {
            selection.source = source;
          }

          const payload = {
            recommendation_id: aiRecommendations.recommendation.id,
            selections: [selection],
          };

          await travelerService.selectRecommendations(travelerId, payload);

          apiCache.invalidate(`traveler:recommendations:${travelerId}:true`);
          apiCache.invalidate(`traveler:recommendations:${travelerId}:false`);

          // Re-fetch to get updated state with isSelected flags
          try {
            const updatedRecommendations = await medicationService.getAIRecommendations(
              travelerId,
              true
            );
            initializeFromRecommendations(updatedRecommendations);
          } catch {
            // Don't fail the selection if re-fetch fails
          }

          setSelectionVersion((prev) => prev + 1);
        } catch (error: any) {
          showAlert(
            "destructive",
            "Save Failed",
            `Failed to save selection: ${error?.message || "Unknown error"}`
          );
          setMedicationSelections((prev) => ({
            ...prev,
            [medicationId]: null,
          }));
        }
      }
    },
    [travelerId, aiRecommendations, medicationNotes, initializeFromRecommendations, showAlert]
  );

  const handleMedicationNoteChange = useCallback(
    (medicationId: string, note: string) => {
      setMedicationNotes((prev) => ({
        ...prev,
        [medicationId]: note,
      }));
    },
    []
  );

  const handleRefillInfoChange = useCallback(
    (medicationId: string, refillInfo: string) => {
      setRefillInfos((prev) => ({
        ...prev,
        [medicationId]: refillInfo,
      }));
    },
    []
  );

  const handleAdministrationSiteChange = useCallback(
    (medicationId: string, site: AdministrationSite | null) => {
      setAdministrationSites((prev) => ({
        ...prev,
        [medicationId]: site,
      }));
    },
    []
  );

  const handleQuantityChange = useCallback(
    (medicationId: string, quantity: string) => {
      setQuantities((prev) => ({
        ...prev,
        [medicationId]: quantity,
      }));
    },
    []
  );

  const handleRecommendationNoteChange = useCallback((note: string) => {
    setPharmacistRecommendationNote(note);
  }, []);

  const saveItemNote = useCallback(
    async (medicationId: string, note: string): Promise<boolean> => {
      if (!travelerId || !aiRecommendations) {
        showAlert("destructive", "Error", "Cannot save note - missing data");
        return false;
      }

      try {
        const parsed = parseMedicationId(medicationId);
        if (!parsed) {
          showAlert("destructive", "Error", "Unknown item type");
          return false;
        }

        const currentSelection = medicationSelections[medicationId];
        const action = currentSelection === false ? "deselect" : "select";

        const selection: RecommendationSelection = {
          item_type: parsed.itemType,
          item_key: parsed.protocolId,
          action: action,
          note: note.trim() || undefined,
        };

        const source = getApiSource(parsed);
        if (source) {
          selection.source = source;
        }

        const payload = {
          recommendation_id: aiRecommendations.recommendation.id,
          selections: [selection],
        };

        await travelerService.selectRecommendations(travelerId, payload);

        apiCache.invalidate(`traveler:recommendations:${travelerId}:true`);
        apiCache.invalidate(`traveler:recommendations:${travelerId}:false`);

        setMedicationNotes((prev) => ({
          ...prev,
          [medicationId]: note,
        }));

        if (currentSelection === null || currentSelection === undefined) {
          setMedicationSelections((prev) => ({
            ...prev,
            [medicationId]: true,
          }));
        }

        // Re-fetch to get updated state with new selectionDetails
        try {
          const updatedRecommendations = await medicationService.getAIRecommendations(
            travelerId,
            true
          );
          initializeFromRecommendations(updatedRecommendations);
        } catch {
          // Don't fail the save operation if re-fetch fails
        }

        setSelectionVersion((prev) => prev + 1);
        showAlert("success", "Saved", "Note saved successfully");
        return true;
      } catch (error: any) {
        showAlert(
          "destructive",
          "Save Failed",
          `Failed to save note: ${error?.message || "Unknown error"}`
        );
        return false;
      }
    },
    [travelerId, aiRecommendations, medicationSelections, initializeFromRecommendations, showAlert]
  );

  const saveItemDetails = useCallback(
    async (
      medicationId: string,
      note: string,
      refillInfo: string,
      administrationSite: AdministrationSite | null,
      quantity: string = ""
    ): Promise<boolean> => {
      if (!travelerId || !aiRecommendations) {
        showAlert("destructive", "Error", "Cannot save details - missing data");
        return false;
      }

      try {
        const parsed = parseMedicationId(medicationId);
        if (!parsed) {
          showAlert("destructive", "Error", "Unknown item type");
          return false;
        }

        const currentSelection = medicationSelections[medicationId];
        const action = currentSelection === false ? "deselect" : "select";

        const selection: RecommendationSelection = {
          item_type: parsed.itemType,
          item_key: parsed.protocolId,
          action: action,
          note: note?.trim() || "",
          refill_info: refillInfo?.trim() || "",
          administration_site: administrationSite || undefined,
          quantity: quantity?.trim() || "",
        };

        const source = getApiSource(parsed);
        if (source) {
          selection.source = source;
        }

        const payload = {
          recommendation_id: aiRecommendations.recommendation.id,
          selections: [selection],
        };

        await travelerService.selectRecommendations(travelerId, payload);

        apiCache.invalidate(`traveler:recommendations:${travelerId}:true`);
        apiCache.invalidate(`traveler:recommendations:${travelerId}:false`);

        setMedicationNotes((prev) => ({
          ...prev,
          [medicationId]: note,
        }));
        setRefillInfos((prev) => ({
          ...prev,
          [medicationId]: refillInfo,
        }));
        setAdministrationSites((prev) => ({
          ...prev,
          [medicationId]: administrationSite,
        }));
        setQuantities((prev) => ({
          ...prev,
          [medicationId]: quantity,
        }));

        if (currentSelection === null || currentSelection === undefined) {
          setMedicationSelections((prev) => ({
            ...prev,
            [medicationId]: true,
          }));
        }

        // Re-fetch to get updated state with new selectionDetails
        try {
          const updatedRecommendations = await medicationService.getAIRecommendations(
            travelerId,
            true
          );
          initializeFromRecommendations(updatedRecommendations);
        } catch {
          // Don't fail the save operation if re-fetch fails
        }

        setSelectionVersion((prev) => prev + 1);
        showAlert("success", "Saved", "Details saved successfully");
        return true;
      } catch (error: any) {
        showAlert(
          "destructive",
          "Save Failed",
          `Failed to save details: ${error?.message || "Unknown error"}`
        );
        return false;
      }
    },
    [travelerId, aiRecommendations, medicationSelections, initializeFromRecommendations, showAlert]
  );

  const saveSelections = useCallback(
    async (manualSelectionsToSave?: RecommendationSelection[]) => {
      if (!travelerId || !aiRecommendations) {
        showAlert("destructive", "Error", "Cannot save selections - missing data");
        return;
      }

      try {
        setSavingSelections(true);

        const selections: RecommendationSelection[] = [];

        Object.entries(medicationSelections).forEach(([key, value]) => {
          if (value === null) return;
          if (key.startsWith("manual-")) return;

          const parsed = parseMedicationId(key);
          if (!parsed) return;

          const selection: RecommendationSelection = {
            item_type: parsed.itemType,
            item_key: parsed.protocolId,
            action: value === true ? "select" : "deselect",
          };

          const note = medicationNotes[key];
          selection.note = note?.trim() || "";

          const refillInfo = refillInfos[key];
          selection.refill_info = refillInfo?.trim() || "";

          const adminSite = administrationSites[key];
          if (adminSite) {
            selection.administration_site = adminSite;
          }

          const quantity = quantities[key];
          selection.quantity = quantity?.trim() || "";

          const source = getApiSource(parsed);
          if (source) {
            selection.source = source;
          }

          selections.push(selection);
        });

        if (manualSelectionsToSave && manualSelectionsToSave.length > 0) {
          selections.push(...manualSelectionsToSave);
        }

        if (selections.length === 0 && !pharmacistRecommendationNote.trim()) {
          showAlert("warning", "No Changes", "No selections have been made");
          return;
        }

        const payload: {
          recommendation_id: string;
          selections: RecommendationSelection[];
          pharmacist_note?: string;
        } = {
          recommendation_id: aiRecommendations.recommendation.id,
          selections,
        };

        if (pharmacistRecommendationNote.trim()) {
          payload.pharmacist_note = pharmacistRecommendationNote.trim();
        }

        await travelerService.selectRecommendations(travelerId, payload);

        apiCache.invalidate(`traveler:recommendations:${travelerId}:true`);
        apiCache.invalidate(`traveler:recommendations:${travelerId}:false`);

        // Re-fetch to get updated state
        try {
          const updatedRecommendations = await medicationService.getAIRecommendations(
            travelerId,
            true
          );
          initializeFromRecommendations(updatedRecommendations);
          setSelectionVersion((prev) => prev + 1);
        } catch {
          // Don't fail the save operation
        }

        showAlert(
          "success",
          "Saved",
          "Medication and vaccine selections have been saved successfully"
        );
      } catch (error) {
        showAlert("destructive", "Error", "Failed to save selections. Please try again.");
      } finally {
        setSavingSelections(false);
      }
    },
    [
      travelerId,
      aiRecommendations,
      medicationSelections,
      medicationNotes,
      refillInfos,
      administrationSites,
      quantities,
      pharmacistRecommendationNote,
      initializeFromRecommendations,
      showAlert,
    ]
  );

  return {
    medicationSelections,
    medicationNotes,
    refillInfos,
    administrationSites,
    quantities,
    pharmacistRecommendationNote,
    aiRecommendations,
    savingSelections,
    selectionVersion,
    setAiRecommendations,
    handleMedicationSelectionChange,
    handleMedicationNoteChange,
    handleRefillInfoChange,
    handleAdministrationSiteChange,
    handleQuantityChange,
    handleRecommendationNoteChange,
    saveItemNote,
    saveItemDetails,
    saveSelections,
    initializeFromRecommendations,
  };
}
